import React, { useCallback } from 'react';
import { Github } from 'lucide-react';
import { useGitHubAuth } from '../hooks/useGitHubAuth';
import { GitHubTokenManager } from '../utils/secureStorage';

const getOAuthBaseUrl = () => {
  const envUrl = (import.meta.env?.VITE_OAUTH_BASE_URL as string | undefined);
  if (envUrl && typeof envUrl === 'string') return envUrl;

  // In production, use the custom domain for OAuth
  return 'https://api.prompts.rbios.net';
};

const OAUTH_BASE_URL = getOAuthBaseUrl();

const GitHubLogin: React.FC = () => {
  const { isLoading } = useGitHubAuth();

  const handleLogin = useCallback(() => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const loginUrl = `${OAUTH_BASE_URL}/oauth/login`;
    const popup = window.open(
      loginUrl,
      'github_oauth',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const messageHandler = async (event: MessageEvent) => {
      // Accept messages only from our OAuth backend origin
      const allowedOrigin = OAUTH_BASE_URL;
      console.debug('[oauth] message received', {
        eventOrigin: event.origin,
        allowedOrigin,
        hasData: !!event.data,
        keys: event.data ? Object.keys(event.data) : [],
      });
      const data = event.data as { source?: string; access_token?: string };
      if (event.origin !== allowedOrigin) {
        console.warn('[oauth] origin mismatch, ignoring');
        return;
      }
      if (data?.source !== 'oauth' || typeof data?.access_token !== 'string') {
        console.warn('[oauth] malformed message, ignoring');
        return;
      }

      try {
        console.info('[oauth] storing token', { length: data.access_token.length });
        await GitHubTokenManager.setAccessToken(data.access_token);
        const readBack = await GitHubTokenManager.getAccessToken();
        console.info('[oauth] token read-back', { length: readBack ? readBack.length : 0 });
        popup?.close();
        // Allow logs to flush so you can see them before reload
        setTimeout(() => { window.location.href = '/'; }, 300);
      } catch (e) {
        console.error('Failed to store access token', e);
      } finally {
        window.removeEventListener('message', messageHandler as (ev: MessageEvent) => void);
      }
    };

    window.addEventListener('message', messageHandler as (ev: MessageEvent) => void);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
        <Github className="w-16 h-16 mx-auto mb-6 text-gray-700" />
        
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Prompt Manager</h1>
        <p className="text-gray-600 mb-8">
          Connect your GitHub account to manage and organize your prompts with ease.
        </p>
        
        <button
          onClick={handleLogin}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <Github className="w-5 h-5" />
              Continue with GitHub
            </>
          )}
        </button>
        
        <p className="mt-6 text-sm text-gray-500">
          By continuing, you agree to our terms of service and privacy policy.
        </p>
      </div>
    </div>
  );
};

export default GitHubLogin;
